<template>
  <div>
    <div class="space-y-4">
      <!-- Header -->
      <div class="flex flex-wrap items-center justify-between gap-4 p-4 bg-white border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 rounded-2xl">
        <div class="flex items-center min-w-0 space-x-3">
          <span class="flex items-center justify-center w-12 h-12 rounded-xl bg-taxiye-100 text-taxiye-600">
            <i class="material-icons-round">local_taxi</i>
          </span>
          <div class="min-w-0">
            <p class="text-xs font-semibold tracking-widest text-gray-600 uppercase">Trip</p>
            <div class="flex items-center gap-2">
              <h2 class="font-mono text-sm text-gray-800 truncate">{{ trip ? trip._id : "—" }}</h2>
              <span
                v-if="trip"
                class="inline-flex items-center px-2 py-1 text-xs font-semibold leading-none capitalize rounded-full"
                :class="statusClass(trip.status)"
              >
                <span class="w-2 h-2 mr-2 rounded-full" :class="statusDotClass(trip.status)"></span>
                {{ prettyStatus(trip.status) }}
              </span>
            </div>
          </div>
        </div>

        <div class="flex items-center">
          <button
            class="inline-flex items-center h-10 px-5 space-x-2 text-sm font-semibold text-white transition-colors duration-150 rounded-xl shadow-sm bg-taxiye-600 hover:bg-taxiye-400"
            @click="openConfirmBox"
            v-if="trip && trip.status == 'STARTED'"
          >
            <i class="text-base material-icons-round">flag</i>
            <span>End trip</span>
          </button>
          <button
            v-else-if="trip && trip.status == 'COMPLETED'"
            @click="sendEmail"
            class="inline-flex items-center h-10 px-5 space-x-2 text-sm font-semibold text-white transition-colors duration-150 rounded-xl shadow-sm bg-taxiye-600 hover:bg-taxiye-400"
          >
            <i class="text-base material-icons-round">mail</i>
            <span>Resend email</span>
          </button>
          <button
            @click="cancelTrip"
            v-else-if="trip && trip.status != 'CANCELLED'"
            class="inline-flex items-center h-10 px-5 space-x-2 text-sm font-semibold text-red-600 transition-colors duration-150 bg-red-50 rounded-xl hover:bg-red-100"
          >
            <i class="text-base material-icons-round">close</i>
            <span>Cancel trip</span>
          </button>
        </div>
      </div>

      <!-- End trip modal -->
      <portal to="modals">
        <transition
          enter-active-class="transition duration-100 ease-out"
          enter-class="transform scale-95 opacity-0"
          enter-to-class="transform scale-100 opacity-100"
          leave-active-class="transition duration-75 ease-in"
          leave-class="transform scale-100 opacity-100"
          leave-to-class="transform scale-95 opacity-0"
        >
          <div
            v-if="confirmOpen"
            @click="closeConfirmBox"
            class="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black bg-opacity-40"
          >
            <div
              @click.stop
              class="w-full max-w-lg overflow-hidden bg-white shadow-xl rounded-2xl"
            >
              <div class="flex items-center px-6 py-4 space-x-3 border-b border-gray-100">
                <span class="flex items-center justify-center w-9 h-9 text-orange-600 rounded-lg bg-orange-50">
                  <i class="text-lg material-icons-round">warning_amber</i>
                </span>
                <h3 class="font-semibold text-gray-800">End trip</h3>
              </div>

              <div class="px-6 py-5 space-y-5">
                <div>
                  <p class="text-sm text-gray-700">Are you sure you want to end this trip?</p>
                  <p class="mt-1 text-xs text-gray-600">This action can not be undone.</p>
                </div>

                <div class="grid grid-cols-2 gap-4">
                  <div class="p-4 space-y-2 border border-gray-100 shadow-sm rounded-xl bg-gray-50">
                    <p class="text-xs font-semibold tracking-wider text-gray-600 uppercase">Estimated</p>
                    <div class="flex justify-between text-sm">
                      <span class="text-gray-500">Distance</span>
                      <span class="font-medium text-gray-800">{{ estimatedDistance.toFixed(2) }}</span>
                    </div>
                    <div class="flex justify-between text-sm">
                      <span class="text-gray-500">Duration</span>
                      <span class="font-medium text-gray-800">{{ estimatedDuration.toFixed(2) }}</span>
                    </div>
                    <div class="flex justify-between text-sm">
                      <span class="text-gray-500">Price</span>
                      <span class="font-medium text-gray-800">{{ estimatedFare.toFixed(2) }}</span>
                    </div>
                  </div>
                  <div class="p-4 space-y-2 border border-gray-100 shadow-sm rounded-xl bg-gray-50">
                    <p class="text-xs font-semibold tracking-wider text-gray-600 uppercase">Actual</p>
                    <div class="flex justify-between text-sm">
                      <span class="text-gray-500">Distance</span>
                      <span class="font-medium text-gray-800">{{ tripDistance.toFixed(2) }}</span>
                    </div>
                    <div class="flex justify-between text-sm">
                      <span class="text-gray-500">Duration</span>
                      <span class="font-medium text-gray-800">{{ tripDuration.toFixed(2) }}</span>
                    </div>
                    <div class="flex justify-between text-sm">
                      <span class="text-gray-500">Price</span>
                      <span class="font-medium text-gray-800">{{ tripFare.toFixed(2) }}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Distance</label>
                  <input
                    type="text"
                    name="totalDistance"
                    id="totalDistance"
                    class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                    v-model="totalDistance"
                  />
                </div>
                <div v-if="trip.type === 'delivery'">
                  <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">OTP code</label>
                  <input
                    type="text"
                    name="clearanceCode"
                    id="clearanceCode"
                    class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                    v-model="clearanceCode"
                  />
                </div>
              </div>

              <div class="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                <button
                  @click="closeConfirmBox"
                  type="button"
                  class="px-4 py-2 text-sm font-medium text-gray-600 rounded-xl hover:text-gray-900 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  @click="endTrip()"
                  type="button"
                  :disabled="loading || (trip.type === 'delivery' && !clearanceCode)"
                  class="inline-flex items-center px-5 py-2 space-x-2 text-sm font-semibold rounded-xl transition-colors duration-150"
                  :class="
                    loading || (trip.type === 'delivery' && !clearanceCode)
                      ? 'bg-green-100 text-green-500 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  "
                >
                  <i v-if="loading" class="text-base material-icons-round spin">sync</i>
                  <span>End trip</span>
                </button>
              </div>
            </div>
          </div>
        </transition>
      </portal>

      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center py-24 bg-white border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 rounded-2xl">
        <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
        <span class="invisible">Loading...</span>
      </div>

      <template v-else-if="trip != null">
        <!-- Participants -->
        <div class="p-5 bg-white border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 rounded-2xl">
          <div class="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3 lg:grid-cols-4">
            <div>
              <p class="text-xs font-semibold tracking-wider text-gray-600 uppercase">Driver</p>
              <div v-if="trip.driver" class="mt-1">
                <p class="text-sm font-medium text-gray-800">{{ formatName(trip.driver.firstName + " " + trip.driver.lastName) }}</p>
                <p class="text-xs text-gray-500">{{ trip.driver.phoneNumber }}</p>
              </div>
              <p v-else class="mt-1 text-sm text-gray-600">—</p>
            </div>
            <div>
              <p class="text-xs font-semibold tracking-wider text-gray-600 uppercase">Passenger</p>
              <div v-if="trip.passenger" class="mt-1">
                <p class="text-sm font-medium text-gray-800">{{ formatName(trip.passenger.firstName + " " + trip.passenger.lastName) }}</p>
                <p class="text-xs text-gray-500">{{ trip.passenger.phoneNumber }}</p>
              </div>
              <p v-else class="mt-1 text-sm text-gray-600">—</p>
            </div>
            <div>
              <p class="text-xs font-semibold tracking-wider text-gray-600 uppercase">Dispatcher</p>
              <p v-if="trip.dispatcher" class="mt-1 text-sm font-medium text-gray-800">
                {{ formatName(trip.dispatcher.firstName + " " + trip.dispatcher.lastName) }}
              </p>
              <p v-else class="mt-1 text-sm text-gray-600">—</p>
            </div>
            <div>
              <p class="text-xs font-semibold tracking-wider text-gray-600 uppercase">Ended / Cancelled by</p>
              <p v-if="trip.dispatcherWhoEnded" class="mt-1 text-sm font-medium text-gray-800">
                {{ formatName(trip.dispatcherWhoEnded.firstName + " " + trip.dispatcherWhoEnded.lastName) }}
              </p>
              <p v-else-if="trip.cancelledBy" class="mt-1 text-sm font-medium text-gray-800">{{ formatName(trip.cancelledBy) }}</p>
              <p v-else class="mt-1 text-sm text-gray-600">—</p>
            </div>
            <div>
              <p class="text-xs font-semibold tracking-wider text-gray-600 uppercase">Vehicle type</p>
              <p class="mt-1 text-sm font-medium text-gray-800">{{ trip.vehicleType ? trip.vehicleType.name : "—" }}</p>
            </div>
            <div>
              <p class="text-xs font-semibold tracking-wider text-gray-600 uppercase">Vehicle</p>
              <div v-if="trip.vehicle" class="mt-1">
                <p class="text-sm font-medium text-gray-800">{{ trip.vehicle.color + " " + trip.vehicle.modelName }}</p>
                <p class="text-xs text-gray-500">{{ trip.vehicle.plateNumber }}</p>
              </div>
              <p v-else class="mt-1 text-sm text-gray-600">—</p>
            </div>
            <div>
              <p class="text-xs font-semibold tracking-wider text-gray-600 uppercase">Pickup time</p>
              <p class="mt-1 text-sm font-medium text-gray-800">{{ trip.pickupTimestamp ? formatDate(trip.pickupTimestamp) : "—" }}</p>
            </div>
            <div>
              <p class="text-xs font-semibold tracking-wider text-gray-600 uppercase">End time</p>
              <p class="mt-1 text-sm font-medium text-gray-800">
                {{
                  trip.endTimestamp
                    ? formatDate(trip.endTimestamp)
                    : trip.status === "CANCELLED"
                    ? "Cancelled trip"
                    : "—"
                }}
              </p>
            </div>
          </div>

          <hr class="my-5 border-gray-100" />

          <div class="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3 lg:grid-cols-4">
            <div>
              <p class="text-xs font-semibold tracking-wider text-gray-600 uppercase">Corporate</p>
              <p class="mt-1 text-sm font-medium text-gray-800">{{ trip.corporate ? trip.corporate.name : "—" }}</p>
            </div>
            <div>
              <p class="text-xs font-semibold tracking-wider text-gray-600 uppercase">Distance</p>
              <p class="mt-1 text-sm font-medium text-gray-800">
                {{ trip && trip.totalDistance ? trip.totalDistance.toFixed(2) + " KM" : "—" }}
              </p>
              <p class="text-xs text-gray-500">
                estimated
                {{ trip.route && trip.route.distance ? (trip.route.distance / 1000).toFixed(2) + " KM" : "—" }}
              </p>
            </div>
            <div>
              <p class="text-xs font-semibold tracking-wider text-gray-600 uppercase">Ticket</p>
              <p class="mt-1 text-sm font-medium text-gray-800">{{ trip.ticket ? trip.ticket.code : "—" }}</p>
            </div>
            <div>
              <p class="text-xs font-semibold tracking-wider text-gray-600 uppercase">Type</p>
              <span class="inline-flex px-2 py-1 mt-1 text-xs font-medium text-gray-600 capitalize rounded-md bg-gray-100">
                {{ trip.type }}
              </span>
            </div>
            <div>
              <p class="text-xs font-semibold tracking-wider text-gray-600 uppercase">Active</p>
              <span
                class="inline-flex px-2 py-1 mt-1 text-xs font-medium rounded-md"
                :class="trip.active ? 'text-green-700 bg-green-100' : 'text-gray-600 bg-gray-100'"
              >
                {{ trip.active ? "Yes" : "No" }}
              </span>
            </div>
          </div>
        </div>

        <!-- Fare breakdown + map -->
        <div class="flex flex-col gap-4 lg:flex-row">
          <div class="w-full lg:w-1/3">
            <div class="p-5 bg-white border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 rounded-2xl">
              <div class="pb-4 mb-2 space-y-3 border-b border-gray-100">
                <div>
                  <p class="text-xs font-semibold tracking-wider text-gray-600 uppercase">From</p>
                  <div class="flex items-start mt-1 space-x-2">
                    <i class="text-base leading-none text-green-500 material-icons-round">my_location</i>
                    <p class="text-sm text-gray-800">{{ trip.pickUpAddress ? trip.pickUpAddress.name : "—" }}</p>
                  </div>
                </div>
                <div>
                  <p class="text-xs font-semibold tracking-wider text-gray-600 uppercase">To</p>
                  <div class="flex items-start mt-1 space-x-2">
                    <i class="text-base leading-none text-red-500 material-icons-round">flag</i>
                    <p class="text-sm text-gray-800">{{ trip.dropOffAddress ? trip.dropOffAddress.name : "—" }}</p>
                  </div>
                </div>
              </div>

              <dl class="divide-y divide-gray-100">
                <div class="flex items-center justify-between py-2.5 text-sm">
                  <dt class="text-gray-500">Cancel cost</dt>
                  <dd class="font-medium text-gray-800">{{ trip.cancelCost ? trip.cancelCost.toFixed(2) : "—" }}</dd>
                </div>
                <div class="flex items-center justify-between py-2.5 text-sm">
                  <dt class="text-gray-500">Commission</dt>
                  <dd class="font-medium text-gray-800">{{ trip.companyCut ? trip.companyCut.toFixed(2) : "—" }}</dd>
                </div>
                <div class="flex items-center justify-between py-2.5 text-sm">
                  <dt class="text-gray-500">Estimated fare</dt>
                  <dd class="font-medium text-gray-800">{{ estimatedFare ? estimatedFare.toFixed(2) : "—" }}</dd>
                </div>
                <div class="flex items-center justify-between py-2.5 text-sm">
                  <dt class="text-gray-500">Fare</dt>
                  <dd class="font-medium text-gray-800">{{ trip.fare ? trip.fare.toFixed(2) : "—" }}</dd>
                </div>
                <div class="flex items-center justify-between py-2.5 text-sm">
                  <dt class="text-gray-500">Passenger fare</dt>
                  <dd class="font-medium text-gray-800">{{ trip.passengerFare ? trip.passengerFare.toFixed(2) : "—" }}</dd>
                </div>
                <div class="flex items-center justify-between py-2.5 text-sm">
                  <dt class="text-gray-500">Tax</dt>
                  <dd class="font-medium text-gray-800">{{ trip.tax ? trip.tax.toFixed(2) : "—" }}</dd>
                </div>
                <div class="flex items-center justify-between py-2.5 text-sm">
                  <dt class="text-gray-500">Discount</dt>
                  <dd class="font-medium text-gray-800">{{ trip.discount ? trip.discount.toFixed(2) : "—" }}</dd>
                </div>
                <div class="flex items-center justify-between py-2.5 text-sm">
                  <dt class="text-gray-500">Paid to driver</dt>
                  <dd class="font-medium text-gray-800">{{ trip.payedToDriver ? trip.payedToDriver.toFixed(2) : "—" }}</dd>
                </div>
                <div class="flex items-center justify-between py-2.5 text-sm">
                  <dt class="font-semibold text-gray-700">Net</dt>
                  <dd class="font-semibold text-taxiye-600">{{ trip.net ? trip.net.toFixed(2) : "—" }}</dd>
                </div>
              </dl>
            </div>
          </div>

          <div class="w-full lg:w-2/3">
            <div
              class="overflow-hidden bg-white border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 rounded-2xl"
              style="height: 520px"
            >
              <MapBox :route1="route1" :route2="route2"></MapBox>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
// import moment from "moment";
import init from "@/gmaps";

const polyline = require("google-polyline");
import MapBox from "../../components/MapBox"


export default {
  data() {
    return {
      trip: null,
      loading: true,
      google: null,
      markers: [],
      totalDistance: null,
      clearanceCode: null,
      serverTimeDifference: 0,
      confirmOpen: false,
      map: null,

      route1: [],
      route2: [],
    };
  },
  components: {
    MapBox,
  },
  watch: {},
  created() {
    this.getServerTime();
    this.getRide();
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    estimatedDistance() {
      return this.trip && this.trip.route && this.trip.route.distance
        ? this.trip.route.distance / 1000
        : 0;
    },
    estimatedDuration() {
      return this.trip && this.trip.route && this.trip.route.duration
        ? this.trip.route.duration / 60
        : 0;
    },
    tripDuration() {
      var tsts = new Date(this.trip.pickupTimestamp);
      // var date = new Date(new Date().getTime() - this.serverTimeDifference);
      var date = new Date(
        this.trip?.endTimestamp
          ? this.trip.endTimestamp
          : new Date().getTime() - this.serverTimeDifference
      );
      return (date.getTime() - tsts.getTime()) / 1000 / 60;
    },
    tripDistance() {
      return this.totalTripDistance();
    },
    tripFare() {
      var distance = this.tripDistance;
      var duration = this.tripDuration;
      if (distance && this.trip && this.trip.vehicleType && duration) {
        return (
          distance * this.trip.vehicleType.pricePerKM +
          duration * this.trip.vehicleType.pricePerMin +
          this.trip.vehicleType.baseFare
        );
      } else {
        return 0;
      }
    },
    estimatedFare() {
      var distance = this.estimatedDistance;
      var duration = this.estimatedDuration;
      if (distance && this.trip && this.trip.vehicleType && duration) {
        return (
          distance * this.trip.vehicleType.pricePerKM +
          duration * this.trip.vehicleType.pricePerMin +
          this.trip.vehicleType.baseFare
        );
      } else {
        return 0;
      }
    },
  },
  async mounted() {
    this.google = await init();

    this.map = new this.google.maps.Map(this.$refs.map, {
      center: {
        lat: 9.004912,
        lng: 38.778282,
      },
      zoom: 13,
      streetViewControl: false,
      mapTypeControl: false,
    });
  },
  methods: {
    statusClass(status) {
      const s = (status || "").toLowerCase();
      if (s.includes("cancel") || s.includes("no_drivers")) return "text-red-700 bg-red-100";
      if (s.includes("complet")) return "text-green-700 bg-green-100";
      if (s.includes("start") || s.includes("running") || s.includes("progress")) return "text-orange-700 bg-orange-100";
      return "text-gray-700 bg-gray-100";
    },
    statusDotClass(status) {
      const s = (status || "").toLowerCase();
      if (s.includes("cancel") || s.includes("no_drivers")) return "bg-red-500";
      if (s.includes("complet")) return "bg-green-500";
      if (s.includes("start") || s.includes("running") || s.includes("progress")) return "bg-orange-500";
      return "bg-gray-400";
    },
    prettyStatus(status) {
      return (status || "—").replace(/_/g, " ").toLowerCase();
    },
    formatName(name) {
      if (!name) return "—";
      return name
        .toLowerCase()
        .split(" ")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    },
    calculateDistance(from, to) {
      if (from.length == 2 && to.length == 2) {
        var R = 6371; // Radius of the earth in km
        var dLat = (Math.PI / 180) * (to[1] - from[1]); // (Math.PI / 180) *  below
        var dLon = (Math.PI / 180) * (to[0] - from[0]);
        var a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos((Math.PI / 180) * from[1]) *
            Math.cos((Math.PI / 180) * to[1]) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
        var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        var d = R * c; // Distance in km
        return d;
      } else {
        return 0;
      }
    },
    getServerTime() {
      this.$misc.get("/date").then((response) => {
        var date = new Date(response.data);
        this.serverTimeDifference = new Date().getTime() - date.getTime();
      });
    },
    openConfirmBox() {
      this.confirmOpen = true;
    },
    closeConfirmBox() {
      this.confirmOpen = false;
    },
    sendEmail() {
      this.loading = true;
      this.$misc
        .get(`trips/${this.id}/send-email`)
        .then((response) => {
          console.log({ response });
          this.loading = false;
        })
        .catch((error) => console.log({ error }))
        .finally(() => (this.loading = false));
    },
    cancelTrip() {
      this.loading = true;
      this.$http
        .post(`trips/${this.id}/cancel`)
        .then((response) => {
          console.log({ response });
          this.trip = response.data;
          this.loading = false;
        })
        .catch((error) => console.log({ error }))
        .finally(() => (this.loading = false));
    },
    formatDate(date) {
      return date ? date.replace("T", " ").slice(0, 16) : "-";

      // return moment(date).format("MMM D, YYYY HH:mm");
    },
    totalTripDistance() {
      var distance = 0;
      if (this.trip && this.trip.path && this.trip.path.length) {
        for (let i = 0; i < this.trip.path.length; i++) {
          if (i + 1 < this.trip.path.length) {
            distance += this.calculateDistance(
              this.trip.path[i],
              this.trip.path[i + 1]
            );
          }
        }
        this.totalDistance = distance.toFixed(2);
        return distance;
      } else {
        return distance;
      }
    },
    resendEmail() {
      this.loading = true;
      this.$misc
        .get(`trips/${this.id}/resend-email`)
        .then((response) => {
          console.log({ response });
          this.getRide();
        })
        .catch((error) => console.log({ error }))
        .finally(() => (this.loading = false));
    },
    endTrip() {
      try {
        if (isNaN(this.totalDistance)) {
          return window.flash({
            type: "error",
            body: `trip distance should be number`,
          });

        }

        var distance = parseFloat(this.totalDistance.toString());

        this.loading = true;
        this.$http
          .post(`trips/${this.id}/end`, { totalDistance: distance, clearanceCode: this.clearanceCode })
          .then((response) => {
            console.log({ response });
            this.getRide()
            this.trip = response.data;
            this.loading = false;
          })
          .catch((error) => {
            this.loading = false;
             window.flash({
               type: "error",
            body: error.response.data,
          });
            console.log({ error })
          })
          .finally(() => (this.loading = false));
      } catch (error) {
        console.log({ error });
      }
    },
    getRide() {
      this.loading = true;
      var path = `trips/${this.id}?limit=populate=["driver","passenger","vehicleType","dispatcher", "corporate"]&start=${this.startDate}&end=${this.endDate}`;
      this.$misc
        .get(path)
        .then((response) => {
          this.trip = response.data;
          this.trip.route.coordinates = polyline.decode(this.trip.route.polyline)
          console.log(this.trip);
          this.totalTripDistance()

          if (this.trip.path && this.trip.path.length > 1) {
            new this.google.maps.Marker({
              position: {
                lat: this.trip.path[0][1],
                lng: this.trip.path[0][0],
              },
              icon:
                "http://res.cloudinary.com/ika/image/upload/w_30,h_50/v1606686565/pxc38df2vfobmgmmxi95.png",
              map: this.map,
            });
            // alert(JSON.stringify({  lat: this.trip.path[0][1],
            //     lng: this.trip.path[0][0],}))

            new this.google.maps.Marker({
              position: {
                lat: this.trip.path[this.trip.path.length - 1][1],
                lng: this.trip.path[this.trip.path.length - 1][0],
              },
              icon:
                "http://res.cloudinary.com/ika/image/upload/w_30,h_50/v1606686806/cot62kytpswv7lzdextc.png",
              map: this.map,
            });
          }

          new this.google.maps.Marker({
            position: {
              lat: this.trip.pickUpAddress.lat,
              lng: this.trip.pickUpAddress.long,
            },
            icon:
              "http://res.cloudinary.com/ika/image/upload/w_15,h_25/v1606686565/pxc38df2vfobmgmmxi95.png",
            map: this.map,
          });

          new this.google.maps.Marker({
            position: {
              lat: this.trip.dropOffAddress.lat,
              lng: this.trip.dropOffAddress.long,
            },
            icon:
              "http://res.cloudinary.com/ika/image/upload/w_15,h_25/v1606686806/cot62kytpswv7lzdextc.png",
            map: this.map,
          });

          var route = this.trip.route.coordinates.map((coord) => {
            return { lat: coord[0], lng: coord[1] };
          });


          this.route1 = this.trip.route.coordinates.map(x=>[x[1], x[0]])
          this.route2 = this.trip.path

          new this.google.maps.Polyline({
            path: route,
            geodesic: true,
            map: this.map,
            strokeColor: "#FF0000",
            strokeOpacity: 1.0,
            strokeWeight: 2,
          });

          var path = this.trip.path.map((coord) => {
            return { lat: coord[1], lng: coord[0] };
          });


          new this.google.maps.Polyline({
            path: path,
            geodesic: true,
            map: this.map,
            strokeColor: "#00FF00",
            strokeOpacity: 1.0,
            strokeWeight: 2,
          });

          this.$http
            .get(`trips/${this.id}/sos`)
            .then((sos) => {
              if (sos.data && sos.data.length) {
                var icon =
                  "https://res.cloudinary.com/torpa/image/upload/w_20,h_20/v1598222724/important-icon-png-26_xte9of.png";
                sos.data.forEach((element) => {
                  new this.google.maps.Marker({
                    position: {
                      lat: element.position.coordinates[0],
                      lng: element.position.coordinates[1],
                    },
                    icon,
                    map: this.map,
                  });
                });
              }
            })
            .catch((error) => {
              console.log({ error });
            });

        })
        .catch((err) => console.log(err))
        .finally(() => (this.loading = false));
    },
  },
};
</script>
